import { DefaultButton, Dropdown, IButtonStyles, ITag, Label, MessageBar, MessageBarType, PrimaryButton, ProgressIndicator, SearchBox, Stack, TagPicker } from "@fluentui/react"
import queryString from "query-string"
import { useEffect, useState } from "react"
import { useApiCall } from "../hooks/apiHooks"
import { Provider, ProviderSpecialty } from "../types/IProvider"

interface ProviderResultProps {
	providerSearch?: string
	specialtySearch?: string[]
	provider: Provider
	setSelectedProvider: (provider: Provider) => void
}

export const cardBoxShadow = "0 1px 2px 0 rgba(5, 10, 15, .09),0 0 3px 0 rgba(5, 10, 15, .15)"

function updateQueryString(key: string, value: string) {
	const currentQuery = queryString.parse(window.location.search)
	value ? (currentQuery[key] = value) : delete currentQuery[key]
	window.history.pushState({}, "", `?${queryString.stringify(currentQuery)}`)
}

function getProviderUrl(providerSearch: string, specialtySearch: string[]) {
	if (!providerSearch && !specialtySearch.length) return ""
	const query = queryString.stringify({ providerSearch, specialtySearch: specialtySearch.join(",") })
	return `/providers?${query}`
}

type ProviderSearchScope = "name" | "specialty"

const scopeButtonStyles: IButtonStyles = {
	label: {
		fontSize: 24
	},
	root: {
		height: 40,
		backgroundColor: "#7bafdc"
	}
}

const suggestedSpecialties = [
	"Cardiology",
	"Dermatology",
	"Family Medicine",
	"Internal Medicine",
	"Neurology",
	"Obstetrics & Gynecology",
	"Oncology",
	"Orthopedics",
	"Pediatrics",
	"Psychiatry",
	"Radiology",
	"Surgery"
]

function getScopeFromUrl() {
	const search = queryString.parse(window.location.search)
	return (search.providerSearch && "name") || (search.specialtySearch && "specialty")
}

export function ProviderSearch() {
	const [providerSearch, setProviderSearch] = useState<string>(() => (queryString.parse(window.location.search).providerSearch as string) || "")
	const [specialtySearch, setSpecialtySearch] = useState<string[]>(() => (queryString.parse(window.location.search).specialtySearch as string)?.split(",") || [])
	const [providerSearchScope, setProviderSearchScope] = useState<ProviderSearchScope>(getScopeFromUrl() || undefined)
	const {
		data: providers,
		setData: setProviders,
		loading,
		error
	} = useApiCall<Provider[]>(getProviderUrl(providerSearch, specialtySearch), {
		canFetch: (providerSearch && providerSearch.length >= 2) || Boolean(specialtySearch?.length),
		delay: 500
	})
	const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null)

	// Clear results when switching scopes
	useEffect(() => {
		if (providerSearchScope === "name" && specialtySearch.length) {
			setSpecialtySearch([])
			updateQueryString("specialtySearch", "")
		} else if (providerSearchScope === "specialty" && providerSearch) {
			setProviderSearch("")
			updateQueryString("providerSearch", "")
		}
	}, [providerSearchScope, setProviders, providerSearch, specialtySearch])

	useEffect(() => {
		if (!providers || loading || selectedProvider) return

		const providerId = queryString.parse(window.location.search).providerId as string
		if (!providerId) return

		const provider = providers.find(p => p.cw_practice_providerid === providerId)
		if (provider) setSelectedProvider(provider)
	}, [providers, loading, selectedProvider])

	if (selectedProvider) return <ProviderDetails provider={selectedProvider} setSelectedProvider={setSelectedProvider} />

	const handleSearchChange = (field: "provider" | "specialty", newValue: string | string[]) => {
		setProviderSearch(prev => (field === "provider" ? (newValue as string) : prev))
		setSpecialtySearch(prev => (field === "specialty" ? (newValue as string[]) : prev))
		updateQueryString(field === "provider" ? "providerSearch" : "specialtySearch", field === "provider" ? (newValue as string) : (newValue as string[]).join(","))
		updateQueryString(field === "provider" ? "specialtySearch" : "providerSearch", "")
	}

	return (
		<Stack tokens={{ childrenGap: 10 }} style={{ width: "100%" }} horizontalAlign="center" wrap grow>
			<Stack style={{ width: "100%" }} horizontal tokens={{ childrenGap: 10 }} wrap grow>
				{providerSearchScope === "name" && (
					<Stack tokens={{ childrenGap: 4 }} grow>
						<Label>Provider Search</Label>
						<SearchBox
							placeholder="Search by practice or provider last name..."
							styles={{ root: { maxWidth: "60rem" } }}
							iconProps={{ iconName: "Search" }}
							onChange={(_, v) => handleSearchChange("provider", v || "")}
							value={providerSearch}
						/>
					</Stack>
				)}
				{providerSearchScope === "specialty" && (
					<SpecialtyTagPicker loading={loading} selectedSpecialties={specialtySearch} setSelectedSpecialties={specialties => handleSearchChange("specialty", specialties)} />
				)}
				{providerSearchScope && (
					<Stack.Item align="end">
						<Dropdown
							options={[
								{ key: "name", text: "Search by name" },
								{ key: "specialty", text: "Search by specialty" }
							]}
							selectedKey={providerSearchScope}
							styles={{
								dropdown: {
									backgroundColor: "#7bafdc"
								},
								title: {
									backgroundColor: "#7bafdc"
								},
								dropdownItemSelected: {
									backgroundColor: "#7bafdc"
								}
							}}
							onChange={(_, item) => setProviderSearchScope(item.key as ProviderSearchScope)}
						/>
					</Stack.Item>
				)}
				{!providerSearchScope && (
					<Stack tokens={{ childrenGap: 8 }} horizontalAlign="center" styles={{ root: { marginLeft: "auto", marginRight: "auto" } }}>
						<h4 style={{ color: "#000" }}>I'd like to</h4>
						<PrimaryButton styles={scopeButtonStyles} text="Search by name" onClick={() => setProviderSearchScope("name")} />
						<div>or</div>
						<PrimaryButton styles={scopeButtonStyles} text="Search by specialty" onClick={() => setProviderSearchScope("specialty")} />
					</Stack>
				)}
			</Stack>
			{error && <MessageBar messageBarType={MessageBarType.error}>There was an error fetching providers: {error}</MessageBar>}
			{loading && <ProgressIndicator label="Loading providers..." styles={{ root: { width: "100%" } }} />}
			{!error && !loading && providers && (
				<Stack tokens={{ childrenGap: "1.5rem" }} style={{ width: "100%" }}>
					{providers.map((provider: Provider) => (
						<ProviderResult
							key={provider.cw_practice_providerid}
							providerSearch={providerSearch}
							specialtySearch={specialtySearch}
							provider={provider}
							setSelectedProvider={setSelectedProvider}
						/>
					))}
				</Stack>
			)}
			{(providerSearch || specialtySearch) && providers?.length === 0 && !loading && <MessageBar messageBarType={MessageBarType.info}>No results</MessageBar>}
		</Stack>
	)
}

const getHighlightedText = (text: string, highlights: string[]) => {
	const regex = new RegExp(`(${highlights.join("|")})`, "gi")
	const parts = text.split(regex)

	return (
		<span>
			{parts.map((part, index) =>
				highlights.some(highlight => highlight && highlight.toLowerCase() === part.toLowerCase()) ? (
					<b key={index} style={{ color: "#000" }}>
						{part}
					</b>
				) : (
					part
				)
			)}
		</span>
	)
}

function ProviderName({ provider, providerSearch }: { provider: Provider; providerSearch?: string }) {
	const lastName = provider.cw_lastname

	return (
		<h3 style={{ margin: 0 }}>
			{provider.cw_title} {provider.cw_firstname} {provider.cw_middlename} <span>{getHighlightedText(lastName, [providerSearch || ""])}</span>
		</h3>
	)
}

function PracticeName({ provider, providerSearch }: { provider: Provider; providerSearch: string }) {
	const practiceName = provider.cw_Account.name

	return <span>{getHighlightedText(practiceName, [providerSearch || ""])}</span>
}

function ProviderResult(props: ProviderResultProps) {
	const { providerSearch, specialtySearch, provider, setSelectedProvider } = props

	const handleClick = () => {
		setSelectedProvider(provider)
		updateQueryString("providerId", provider.cw_practice_providerid)
	}

	return (
		<Stack
			onClick={handleClick}
			styles={{
				root: {
					boxShadow: cardBoxShadow,
					backgroundColor: "#FFF",
					padding: "1rem",
					borderRadius: 2,
					cursor: "pointer",
					width: "100%"
				}
			}}>
			<ProviderName provider={provider} providerSearch={providerSearch} />
			{provider.cw_Account && (
				<p style={{ margin: 0, fontSize: 16 }}>
					<PracticeName provider={provider} providerSearch={providerSearch} />
					{provider.cw_Account.address1_city && (
						<span style={{ color: "#000" }}>
							— {provider.cw_Account.address1_city}, {provider.cw_Account.address1_stateorprovince} {provider.cw_Account.address1_postalcode}
						</span>
					)}
					<br />
					{!!provider.specialties.length && (
						<span>
							Specialties:{" "}
							<span style={{ color: "#0B7F99" }}>
								{provider.specialties.map((s, index) => (
									<span key={index}>
										{getHighlightedText(s.cw_name, specialtySearch)}
										{index < provider.specialties.length - 1 ? ", " : ""}
									</span>
								))}
							</span>
						</span>
					)}
				</p>
			)}
		</Stack>
	)
}

function ProviderDetails(props: ProviderResultProps) {
	const { setSelectedProvider } = props

	const handleBack = () => {
		setSelectedProvider(null)
		updateQueryString("providerId", "")
	}

	return (
		<Stack>
			<Stack horizontal horizontalAlign="space-between" verticalAlign="end">
				<Stack.Item>
					<h1>Provider Details</h1>
				</Stack.Item>
				<Stack.Item styles={{ root: { marginBottom: "1rem" } }}>
					<DefaultButton text="Back to search results" onClick={handleBack} styles={{ root: { minWidth: 132 } }} />
				</Stack.Item>
			</Stack>
			<ProviderInfoCard {...props} />
		</Stack>
	)
}

function ProviderInfoDetail({ label, value }: { label: string; value: string | string[] }) {
	const displayValue = Array.isArray(value) ? value.join(", ") : value
	return (
		<Stack styles={{ root: { margin: "2rem 3rem", maxWidth: "20rem" } }}>
			<div style={{ fontWeight: "bold" }}>{label}</div>
			<div>{displayValue}</div>
		</Stack>
	)
}

function ProviderInfoCard(props: ProviderResultProps) {
	const { provider } = props
	return (
		<Stack styles={{ root: { width: "100%", boxShadow: cardBoxShadow, backgroundColor: "#FFF", minWidth: 526 } }}>
			<Stack horizontal styles={{ root: { borderBottom: "1px solid rgba(9,9,9,.125)" } }}>
				<div style={{ height: "100%", width: "60%", padding: "2rem" }}>
					<ProviderName provider={provider} />
				</div>
				<Stack styles={{ root: { height: "100%", width: "40%", padding: "2rem", overflow: "hidden", borderLeft: "1px solid rgba(9,9,9,.125)" } }}>
					<b>{provider.cw_Account.name}</b>
					{provider.cw_Account.address1_line1 && provider.cw_Account.address1_city && provider.cw_Account.address1_stateorprovince && provider.cw_Account.address1_postalcode && (
						<>
							<div>{provider.cw_Account.address1_line1}</div>
							<div>{provider.cw_Account.address1_line2}</div>
							<div>{provider.cw_Account.address1_line3}</div>
							<div>
								{provider.cw_Account.address1_city}, {provider.cw_Account.address1_stateorprovince} {provider.cw_Account.address1_postalcode}
							</div>
							<br />
						</>
					)}
					<a style={{ textOverflow: "ellipsis", whiteSpace: "nowrap" }} href={provider.cw_Account.websiteurl}>
						{provider.cw_Account.websiteurl}
					</a>
					{provider.cw_Account.telephone1 && <div>Tel: {provider.cw_Account.telephone1}</div>}
					{provider.cw_Account.fax && <div>Fax: {provider.cw_Account.fax}</div>}
				</Stack>
			</Stack>
			{provider.cw_languagesspoken || provider["cw_gender@OData.Community.Display.V1.FormattedValue"] || provider.specialties.length > 0 ? (
				<>
					<Stack horizontal styles={{ root: { padding: "2rem" } }}>
						{provider.cw_languagesspoken && <ProviderInfoDetail label="Languages Spoken" value={provider.cw_languagesspoken} />}
						{provider["cw_gender@OData.Community.Display.V1.FormattedValue"] && (
							<ProviderInfoDetail label="Gender" value={provider["cw_gender@OData.Community.Display.V1.FormattedValue"]} />
						)}
						{provider.specialties.length > 0 && <ProviderInfoDetail label="Specialties" value={provider.specialties.map(v => v.cw_name)} />}
					</Stack>
				</>
			) : (
				<Stack horizontal styles={{ root: { padding: "2rem" } }}>
					No additional details were found
				</Stack>
			)}
		</Stack>
	)
}

interface SpecialtyTagPickerProps {
	selectedSpecialties: string[]
	setSelectedSpecialties: (specialties: string[]) => void
	loading: boolean
}

function SpecialtyTagPicker({ selectedSpecialties, setSelectedSpecialties, loading }: SpecialtyTagPickerProps) {
	const { data: specialties = [] } = useApiCall<ProviderSpecialty[]>("/specialties")
	const selectedItems = (selectedSpecialties || []).map(s => ({ key: s, name: s }))

	return (
		<Stack horizontal tokens={{ childrenGap: 10 }} grow>
			<Stack.Item grow>
				<Stack horizontal tokens={{ childrenGap: 10 }} grow>
					<Stack tokens={{ childrenGap: 4 }} grow styles={{ root: { width: "100%", maxWidth: "60rem" } }}>
						<Label>Specialties Search</Label>
						<TagPicker
							disabled={loading}
							selectedItems={selectedItems}
							onChange={(items?: ITag[]) => setSelectedSpecialties(items?.map(i => specialties.find(s => s.cw_name === i.name).cw_name) || [])}
							onResolveSuggestions={function (filter: string, selectedItems?: ITag[]): ITag[] | PromiseLike<ITag[]> {
								return filter
									? (specialties || [])
											.filter(s => s.cw_name.toLowerCase().includes(filter.toLowerCase()))
											.map(s => ({ key: s.cw_specialtyid, name: s.cw_name }))
											.filter(t => !(selectedItems.map(si => si.key) || []).includes(t.key))
									: []
							}}
							onEmptyResolveSuggestions={() =>
								(specialties || []).filter(s => suggestedSpecialties.map(s => s.toLowerCase()).includes(s.cw_name.toLowerCase())).map(s => ({ key: s.cw_specialtyid, name: s.cw_name }))
							}
							pickerSuggestionsProps={{ mostRecentlyUsedHeaderText: "Suggested Specialties" }}
							resolveDelay={300}
						/>
					</Stack>
					{selectedSpecialties.length > 0 && (
						<Stack.Item align="end">
							<DefaultButton text="Clear" onClick={() => setSelectedSpecialties([])} />
						</Stack.Item>
					)}
				</Stack>
			</Stack.Item>
		</Stack>
	)
}
